<script lang="ts">
    // Update props to include current project info and full project list
    export let currentProject: { title: string; category: string; link: string };
    export let allProjects: Array<{ 
        title: string; 
        category: string; 
        link: string;
        isVisible?: boolean;
    }>;

    // Function to get next/prev visible projects
    function getAdjacentProjects() {
        // Filter only visible projects
        const visibleProjects = allProjects.filter(p => p.isVisible !== false);
        
        if (visibleProjects.length === 0) {
            return {
                prevProject: currentProject,
                nextProject: currentProject
            };
        }
        
        // Find current project index
        const currentIndex = visibleProjects.findIndex(p => p.title === currentProject.title);
        
        // If current project is not found in visible projects, return first and second visible projects
        if (currentIndex === -1) {
            return {
                prevProject: visibleProjects[visibleProjects.length - 1],
                nextProject: visibleProjects[0]
            };
        }
        
        // First try to find projects in the same category
        const sameCategory = visibleProjects.filter(p => 
            p.category === currentProject.category && 
            p.title !== currentProject.title
        );
        
        let prevProject;
        let nextProject;

        if (sameCategory.length > 0) {
            // Find position in same category array
            const currentCategoryIndex = sameCategory.findIndex(p => p.title === currentProject.title);
            
            // Get prev/next from same category if available
            prevProject = sameCategory.length > 0 ? 
                sameCategory[(currentCategoryIndex - 1 + sameCategory.length) % sameCategory.length] :
                null;
            nextProject = sameCategory.length > 0 ? 
                sameCategory[(currentCategoryIndex + 1) % sameCategory.length] :
                null;
        }

        // If we don't have category matches, fall back to the full visible project list
        if (!prevProject) {
            prevProject = visibleProjects[(currentIndex - 1 + visibleProjects.length) % visibleProjects.length];
        }
        if (!nextProject) {
            nextProject = visibleProjects[(currentIndex + 1) % visibleProjects.length];
        }

        return { prevProject, nextProject };
    }

    $: ({ prevProject, nextProject } = getAdjacentProjects());

    import { reveal } from 'svelte-reveal';
</script>

<nav class="border-t mt-16 md:mt-32 border-gray-100" use:reveal={{ blur: 10 }}>
    <div class="container mx-auto px-4 md:px-8">
        <div class="flex justify-between -mt-px">
            <a href={prevProject.link} 
               class="w-1/2 group py-6 pr-2 flex items-center border-t-2 border-transparent hover:border-[#F96604] transition-colors">
                <div class="flex items-center text-gray-500 group-hover:text-[#F96604]">
                    <svg class="w-3 h-3 sm:w-4 sm:h-4 mr-2 transition-transform group-hover:-translate-x-1" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 8H1M1 8L8 15M1 8L8 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <div>
                        <div class="text-xs uppercase tracking-wider mb-1">Previous</div>
                        <div class="font-light">{prevProject.title}</div>
                        <div class="text-xs text-gray-400">{prevProject.category}</div>
                    </div>
                </div>
            </a>
            
            <a href={nextProject.link} 
               class="w-1/2 group py-6 pl-2 flex items-center justify-end border-t-2 border-transparent hover:border-[#F96604] transition-colors">
                <div class="flex items-center text-right text-gray-500 group-hover:text-[#F96604]">
                    <div>
                        <div class="text-xs uppercase tracking-wider mb-1">Next</div>
                        <div class="font-light">{nextProject.title}</div>
                        <div class="text-xs text-gray-400">{nextProject.category}</div>
                    </div>
                    <svg class="w-3 h-3 sm:w-4 sm:h-4 ml-2 transition-transform group-hover:translate-x-1" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 8H15M15 8L8 1M15 8L8 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
            </a>
        </div>
    </div>
</nav> 